/**
 * gc-session-store — keeps an unsaved GameChanger import alive across app restarts.
 *
 * On iPhone the coach shares one file from GameChanger, comes back for the next,
 * and shares again. iOS is free to discard Dugout's web view while they're in
 * GameChanger, which used to throw away the file they had already sent. The parsed
 * sources (plain JSON, already parsed, so nothing is re-read) are kept here in
 * IndexedDB until the import is saved or the coach starts over, for up to a day.
 *
 * Best-effort throughout: any storage failure just means no restore.
 */

const DB_NAME = 'dugout-gc-import';
const STORE = 'sessions';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface StoredSession {
  savedAt: number;
  data: any;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T | undefined> {
  try {
    const db = await open();
    return await new Promise<T | undefined>((resolve) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => resolve(undefined);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return undefined;
  }
}

export async function loadImportSession(key: string): Promise<any | null> {
  const row = await run<StoredSession>('readonly', s => s.get(key));
  if (!row || Date.now() - row.savedAt > MAX_AGE_MS) return null;
  return row.data;
}

export async function saveImportSession(key: string, data: any): Promise<void> {
  // Structured clone can't take functions or class instances; the sources are
  // plain parse output, but round-trip through JSON so a stray value can't
  // abort the write.
  const value: StoredSession = { savedAt: Date.now(), data: JSON.parse(JSON.stringify(data)) };
  await run('readwrite', s => s.put(value, key));
}

export async function clearImportSession(key: string): Promise<void> {
  await run('readwrite', s => s.delete(key));
}
